import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import { HOME } from "@/constants/testIds";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { EnvelopeSimple, PaperPlaneTilt, ShareNetwork, WhatsappLogo, XLogo, Link as LinkIcon, Check } from "@phosphor-icons/react";

export default function NewsletterCTA() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [fromEmail, setFromEmail] = useState("");
  const [friends, setFriends] = useState("");
  const [sharing, setSharing] = useState(false);
  const [copied, setCopied] = useState(false);

  // If we landed via ?ref=<email>, remember the inviter so newsletter subscribes
  // attach the credit automatically.
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const ref = p.get("ref");
    if (ref) localStorage.setItem("ve_referrer", ref);
  }, []);

  const referralLink = () => {
    if (!fromEmail) return "";
    const base =
      typeof window !== "undefined" ? `${window.location.protocol}//${window.location.host}` : "";
    return `${base}/?ref=${encodeURIComponent(fromEmail)}`;
  };

  const shareText = () =>
    `Hey — check out Vape Essence 🚀 Premium disposable vapes & nicotine pouches, 48-72hr delivery in SA. Use my link and we both get 500 loyalty points on your first order: ${referralLink()}`;

  const openWhatsapp = () => {
    if (!fromEmail) return toast.error("Enter your email first — that's how we credit you");
    window.open(
      `https://wa.me/?text=${encodeURIComponent(shareText())}`,
      "_blank",
      "noopener,noreferrer"
    );
  };
  const openX = () => {
    if (!fromEmail) return toast.error("Enter your email first — that's how we credit you");
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText())}`,
      "_blank",
      "noopener,noreferrer"
    );
  };
  const copyLink = async () => {
    if (!fromEmail) return toast.error("Enter your email first — that's how we credit you");
    try {
      await navigator.clipboard.writeText(referralLink());
      setCopied(true);
      toast.success("Referral link copied");
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error("Couldn't copy. Long-press to select instead.");
    }
  };

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const referrer = localStorage.getItem("ve_referrer") || undefined;
      const { data } = await api.post("/newsletter/subscribe", { email, referrer_email: referrer });
      toast.success(data.already ? "You're already in the club" : "Welcome — deals inbound", {
        description: referrer
          ? `You + ${referrer} unlock 500 pts each on your first order.`
          : "Watch your inbox for flavour drops & discount codes.",
      });
      setEmail("");
    } catch {
      toast.error("Couldn't subscribe. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const share = async (e) => {
    e.preventDefault();
    const emails = friends.split(/[,\s]+/).map((s) => s.trim()).filter(Boolean);
    if (!fromEmail || emails.length === 0) {
      toast.error("Add your email and at least one friend");
      return;
    }
    setSharing(true);
    try {
      const { data } = await api.post("/newsletter/share", {
        from_email: fromEmail,
        to_emails: emails,
      });
      toast.success(`Invited ${data.invited} / ${data.total_requested} friends`);
      setFriends("");
    } catch {
      toast.error("Share failed. Check emails and try again.");
    } finally {
      setSharing(false);
    }
  };

  return (
    <section className="relative border-y border-white/10 bg-[#080808]">
      <div className="mx-auto grid max-w-[1400px] gap-10 px-6 py-24 lg:grid-cols-2 lg:px-10 lg:py-32">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <div className="font-label text-xs text-[#FF5A1F]">/ THE INSIDE ESSENCE</div>
          <h2 className="mt-3 font-display text-4xl font-black tracking-tighter lg:text-5xl">
            Flavour drops. <br />
            Discount codes. <br />
            Zero spam.
          </h2>
          <p className="mt-6 max-w-md text-sm leading-relaxed text-white/60">
            Join the newsletter and get first access to new arrivals, sold-out
            restocks, and members-only pricing.
          </p>
          <form onSubmit={subscribe} className="mt-8 flex max-w-md gap-2">
            <div className="relative flex-1">
              <EnvelopeSimple
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40"
              />
              <input
                data-testid={HOME.newsletterEmail}
                type="email"
                required
                placeholder="you@domain.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-white/15 bg-black py-3 pl-9 pr-4 text-sm outline-none focus:border-[#00FFC2]"
              />
            </div>
            <button
              data-testid={HOME.newsletterSubmit}
              disabled={loading}
              className="border border-[#00FFC2] bg-[#00FFC2] px-6 font-label text-xs text-black transition-transform hover:-translate-y-0.5 disabled:opacity-60"
            >
              {loading ? "..." : "Subscribe"}
            </button>
          </form>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="border border-white/10 bg-black p-8"
        >
          <div className="flex items-center gap-3">
            <ShareNetwork size={22} className="text-[#FFC900]" />
            <div className="font-display text-xl font-bold">Share with friends</div>
          </div>
          <p className="mt-3 text-sm text-white/50">
            Both you and each friend get <b className="text-[#FFC900]">500 loyalty points (R50)</b> when
            their first order clears. Zero limits — invite as many as you want.
          </p>
          <form onSubmit={share} className="mt-6 space-y-3">
            <input
              data-testid={HOME.shareFromInput}
              type="email"
              required
              placeholder="Your email"
              value={fromEmail}
              onChange={(e) => setFromEmail(e.target.value)}
              className="w-full border border-white/15 bg-black py-3 px-4 text-sm outline-none focus:border-[#FF5A1F]"
            />
            <textarea
              data-testid={HOME.shareInput}
              placeholder="friend1@email.com, friend2@email.com"
              value={friends}
              onChange={(e) => setFriends(e.target.value)}
              rows={2}
              className="w-full border border-white/15 bg-black py-3 px-4 text-sm outline-none focus:border-[#FF5A1F]"
            />
            <button
              type="submit"
              data-testid={HOME.shareSubmit}
              disabled={sharing}
              className="inline-flex items-center gap-2 border border-[#FF5A1F] bg-[#FF5A1F] px-6 py-3 font-label text-xs text-black transition-transform hover:-translate-y-0.5 disabled:opacity-60"
            >
              <PaperPlaneTilt size={14} weight="bold" />
              {sharing ? "Sending..." : "Send invites"}
            </button>
          </form>

          {/* Quick-share buttons (WhatsApp / X / copy link) */}
          <div className="mt-6 border-t border-white/10 pt-6">
            <div className="font-label text-[10px] text-white/40 mb-3">
              OR SHARE YOUR PERSONAL LINK
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={openWhatsapp}
                data-testid="share-whatsapp"
                className="group inline-flex items-center gap-2 border border-white/15 bg-black px-4 py-2 font-label text-[11px] text-white/80 hover:border-[#25D366] hover:text-[#25D366] transition-colors"
              >
                <WhatsappLogo size={16} weight="fill" className="text-[#25D366]" />
                WhatsApp
              </button>
              <button
                type="button"
                onClick={openX}
                data-testid="share-x"
                className="inline-flex items-center gap-2 border border-white/15 bg-black px-4 py-2 font-label text-[11px] text-white/80 hover:border-white hover:text-white transition-colors"
              >
                <XLogo size={14} weight="fill" />
                X / Twitter
              </button>
              <button
                type="button"
                onClick={copyLink}
                data-testid="share-copylink"
                className="inline-flex items-center gap-2 border border-white/15 bg-black px-4 py-2 font-label text-[11px] text-white/80 hover:border-[#00FFC2] hover:text-[#00FFC2] transition-colors"
              >
                {copied ? <Check size={14} weight="bold" /> : <LinkIcon size={14} weight="bold" />}
                {copied ? "Copied" : "Copy link"}
              </button>
            </div>
            {fromEmail && (
              <div
                data-testid="share-link-preview"
                className="mt-3 truncate border border-dashed border-white/10 bg-black/50 px-3 py-2 font-mono text-[11px] text-white/50"
              >
                {referralLink()}
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
